/**
 * eventBus.ts
 * Immutable append-only event log for MSapling frontend.
 * Every UI state change is an immutable event that enables undo/redo and
 * session resumption.
 *
 * Uses Zustand for reactivity. Events are Object.freeze()'d on append.
 * Capacity: MAX_EVENTS (oldest events dropped when exceeded).
 */
import { create } from 'zustand';

// ─── Event types ─────────────────────────────────────────────────────────────

export type EventType =
  | 'CHAT_MESSAGE_SENT'
  | 'CHAT_MESSAGE_RECEIVED'
  | 'CHAT_STREAM_STARTED'
  | 'CHAT_STREAM_CHUNK'
  | 'CHAT_STREAM_ENDED'
  | 'CHAT_STREAM_CANCELLED'
  | 'CHAT_STREAM_RESUMED'
  | 'CHAT_ERROR'
  | 'MODEL_CHANGED'
  | 'TOKEN_COUNTED'
  | 'THINKING_BLOCK_STARTED'
  | 'THINKING_BLOCK_ENDED'
  | 'PROJECT_SWITCHED'
  | 'CREDITS_UPDATED'
  | 'RATE_LIMITED'
  | 'GUEST_CONVERSION_PROMPTED'
  | 'CHAT_SETTINGS_REQUESTED'
  | 'CHAT_HISTORY_REQUESTED'
  | 'CHAT_ARTIFACTS_REQUESTED'
  | 'CAP_STATUS_UPDATE'
  | 'UPGRADE_REQUIRED'
  | 'CHAT_STREAM_LOCKED'
  | 'COMPLETION_NOT_BILLED'
  | 'tool_argument_delta'
  // Legacy event types kept for backward compatibility
  | 'ui_state_change'
  | 'chat_message_sent'
  | 'chat_message_received'
  | 'stream_token'
  | 'stream_complete'
  | 'model_changed'
  | 'session_started'
  | 'session_ended'
  | 'auth_state_change'
  | 'error_occurred'
  | 'thinking_block'
  | 'tool_use'
  | 'cost_update';

export interface BusEvent<T = unknown> {
  readonly id: string;
  readonly type: EventType;
  readonly payload: T;
  readonly timestamp: number;   // Date.now()
  readonly sessionId: string;   // current chat session ID
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Maximum number of events retained in memory (oldest dropped when exceeded). */
export const MAX_EVENTS = 10_000;

/** Events that support undo/redo. */
export const REVERSIBLE_EVENTS: EventType[] = ['MODEL_CHANGED', 'PROJECT_SWITCHED'];

// ─── Store interface ──────────────────────────────────────────────────────────

interface EventBusState {
  events: BusEvent[];
  sessionId: string;

  // Undo/redo stacks (event IDs)
  undoStack: string[];
  redoStack: string[];
  canUndo: boolean;
  canRedo: boolean;

  // Legacy compat fields (used by existing code)
  maxEvents: number;

  // Actions
  emit: <T>(type: EventType, payload: T) => void;
  getByType: (type: EventType) => BusEvent[];
  getBySession: (sessionId: string) => BusEvent[];
  replayFrom: (eventId: string) => BusEvent[];
  clearSession: () => void;

  // Undo / redo
  undo: () => void;
  redo: () => void;

  // Legacy compat actions
  getEventsByType: (type: EventType) => BusEvent[];
  getEventsSince: (timestamp: number) => BusEvent[];
  replayEvents: (fromTimestamp: number) => BusEvent[];
  clearEvents: () => void;
  setSessionId: (id: string) => void;
  setMaxEvents: (max: number) => void;
}

// ─── ID generation ────────────────────────────────────────────────────────────

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function generateSessionId(): string {
  return `sess-${generateId()}`;
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useEventBus = create<EventBusState>((set, get) => ({
  events: [],
  sessionId: generateSessionId(),
  undoStack: [],
  redoStack: [],
  canUndo: false,
  canRedo: false,
  maxEvents: MAX_EVENTS,

  emit<T>(type: EventType, payload: T): void {
    const { events, sessionId, undoStack } = get();
    const cap = MAX_EVENTS;

    const event = Object.freeze({
      id: generateId(),
      type,
      payload: Object.freeze(typeof payload === 'object' && payload !== null
        ? { ...(payload as Record<string, unknown>) }
        : payload) as T,
      timestamp: Date.now(),
      sessionId,
    }) as BusEvent<T>;

    // Append and trim to capacity — oldest events dropped first
    const next = events.length >= cap
      ? [...events.slice(events.length - cap + 1), event]
      : [...events, event];

    // Update undo stack for reversible events
    let newUndoStack = undoStack;
    if (REVERSIBLE_EVENTS.includes(type)) {
      newUndoStack = [...undoStack, event.id];
    }

    set({
      events: next,
      undoStack: newUndoStack,
      redoStack: [],          // any new action clears redo history
      canUndo: newUndoStack.length > 0,
      canRedo: false,
    });
  },

  getByType(type: EventType): BusEvent[] {
    return get().events.filter((e) => e.type === type);
  },

  getBySession(sessionId: string): BusEvent[] {
    return get().events.filter((e) => e.sessionId === sessionId);
  },

  replayFrom(eventId: string): BusEvent[] {
    const { events } = get();
    const idx = events.findIndex((e) => e.id === eventId);
    if (idx === -1) return [];
    return events.slice(idx + 1);
  },

  clearSession(): void {
    set({
      sessionId: generateSessionId(),
      undoStack: [],
      redoStack: [],
      canUndo: false,
      canRedo: false,
      // events are kept — history is preserved across sessions
    });
  },

  undo(): void {
    const { undoStack, redoStack } = get();
    if (undoStack.length === 0) return;
    const lastId = undoStack[undoStack.length - 1];
    const newUndoStack = undoStack.slice(0, -1);
    const newRedoStack = [...redoStack, lastId];
    set({
      undoStack: newUndoStack,
      redoStack: newRedoStack,
      canUndo: newUndoStack.length > 0,
      canRedo: newRedoStack.length > 0,
    });
  },

  redo(): void {
    const { undoStack, redoStack } = get();
    if (redoStack.length === 0) return;
    const lastId = redoStack[redoStack.length - 1];
    const newRedoStack = redoStack.slice(0, -1);
    const newUndoStack = [...undoStack, lastId];
    set({
      undoStack: newUndoStack,
      redoStack: newRedoStack,
      canUndo: newUndoStack.length > 0,
      canRedo: newRedoStack.length > 0,
    });
  },

  // ─── Legacy compat ────────────────────────────────────────────────────────

  getEventsByType(type: EventType): BusEvent[] {
    return get().events.filter((e) => e.type === type);
  },

  getEventsSince(timestamp: number): BusEvent[] {
    return get().events.filter((e) => e.timestamp > timestamp);
  },

  replayEvents(fromTimestamp: number): BusEvent[] {
    return get().events.filter((e) => e.timestamp >= fromTimestamp);
  },

  clearEvents(): void {
    set({ events: [] });
  },

  setSessionId(id: string): void {
    set({ sessionId: id });
  },

  setMaxEvents(_max: number): void {
    // maxEvents field kept for legacy compat; effective cap is MAX_EVENTS constant
    set({ maxEvents: _max });
  },
}));
